import {
  createContext,
  useContext,
  useState,
  useCallback,
  useMemo,
  ReactNode,
} from "react";
import fetchWithCredentials from "apiClient";
import { BoardMember } from "types/BoardMember";

interface BoardMembersContextType {
  boardMembers: BoardMember[];
  loading: boolean;
  error: string | null;
  refresh: (condoId: number | null) => Promise<void>;
  boardMembersCount: number;
  markContactAsRead: (contactId: number) => void;
  updateLastReadAt: (contactId: number, lastReadAt: string) => void;
}

const BoardMembersContext = createContext<BoardMembersContextType | undefined>(
  undefined,
);

export function BoardMembersProvider({ children }: { children: ReactNode }) {
  const [boardMembers, setBoardMembers] = useState<BoardMember[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  const fetchBoardMembers = useCallback(async (condoId: number | null) => {
    if (!condoId) {
      setBoardMembers([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const data = await fetchWithCredentials(
        `/manager-api/board-members/?condo=${condoId}`,
      );
      setBoardMembers(Array.isArray(data) ? data : []);
    } catch (err: any) {
      console.error(err);
      setError(err.message || "Error fetching board members");
      setBoardMembers([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const refresh = useCallback(
    async (condoId: number | null) => {
      await fetchBoardMembers(condoId);
    },
    [fetchBoardMembers],
  );

  const markContactAsRead = useCallback((contactId: number) => {
    setBoardMembers((prev) =>
      prev.map((member) =>
        member.contact.id === contactId
          ? { ...member, hasUnread: false }
          : member,
      ),
    );
  }, []);

  const updateLastReadAt = useCallback(
    (contactId: number, lastReadAt: string) => {
      setBoardMembers((prev) =>
        prev.map((member) =>
          member.contact.id === contactId ? { ...member, lastReadAt } : member,
        ),
      );
    },
    [],
  );

  const boardMembersCount = useMemo(
    () => (Array.isArray(boardMembers) ? boardMembers.length : 0),
    [boardMembers],
  );

  return (
    <BoardMembersContext.Provider
      value={{
        boardMembers,
        loading,
        error,
        refresh,
        boardMembersCount,
        markContactAsRead,
        updateLastReadAt,
      }}
    >
      {children}
    </BoardMembersContext.Provider>
  );
}

export function useBoardMembers() {
  const context = useContext(BoardMembersContext);
  if (!context) {
    throw new Error(
      "useBoardMembers must be used within a BoardMembersProvider",
    );
  }
  return context;
}
